/**
 * Get competitor's full name (first and last name) from competitor registration
 */
function getCompetitorName (competitorReg) {
  if (!competitorReg) return ''

  // Handle team registrations
  if (competitorReg.isTeam && competitorReg.teamMembers && competitorReg.teamMembers.length > 0) {
    const teamMembers = competitorReg.teamMembers.map(member => {
      const name = `${member.firstName || ''} ${member.lastName || ''}`.trim()
      const gender = member.isMale ? 'M' : 'Ž'
      const numberPrefix = member.assignedNumber ? `#${member.assignedNumber} ` : ''
      return `${numberPrefix}${name} (${gender})`
    }).join(', ')
    return teamMembers
  }

  // Handle individual competitors
  if (!competitorReg.competitor) return ''

  const competitor = competitorReg.competitor
  const firstName = competitor.firstName || ''
  const lastName = competitor.lastName || ''
  const name = `${firstName} ${lastName}`.trim()
  const gender = competitor.isMale === true ? ' (M)' : (competitor.isMale === false ? ' (Ž)' : '')
  const assignedNumber = competitorReg.assignedNumber ? ` (#${competitorReg.assignedNumber})` : ''
  return name ? `${name}${gender}${assignedNumber}` : ''
}

/**
 * Get competitor's club name from competitor registration
 */
function getCompetitorClub (competitorReg) {
  if (!competitorReg || !competitorReg.club) return ''
  const club = competitorReg.club
  return (club.shortName || club.name || '').trim()
}

/**
 * Generates a bracket skeleton HTML structure for printing
 * Based on single-elimination tournament bracket format
 * Matches the structure shown in the reference image: 16-participant bracket
 */
function isTeamBracket (bracket) {
  return bracket.teamDiscipline !== null &&
    bracket.teamDiscipline !== undefined &&
    bracket.teamDiscipline !== '' &&
    bracket.teamDiscipline !== 0
}

export function generateBracketSkeletonHTML (bracket, placement, tournament) {
  if (!bracket || !bracket.matches || bracket.matches.length === 0) {
    return ''
  }

  const teamBracket = isTeamBracket(bracket)

  // Calculate number of rounds and participants
  const rounds = calculateRounds(bracket.matches)

  // Calculate match positions (larger boxes for team brackets so all names fit)
  const matchPositions = calculateMatchPositions(rounds, teamBracket)

  // Calculate container dimensions
  const baseHeight = Math.max(...Object.values(matchPositions).map(p => p.top + p.height), 0) + 100
  // Add extra space for winner field (180px + 20px gap)
  const baseWidth = rounds.length * 310 + 200 + 20 // 250px per round + 60px gap + winner box (180px) + gap (20px)

  // Calculate scaling to fit on one page (A4: 210mm x 297mm, ~794px x 1123px at 96dpi)
  // Account for margins and other content (header ~100px, placement ~150px, signatures ~100px)
  const maxPrintWidth = 750 // Available width for bracket
  const maxPrintHeight = 800 // Available height for bracket
  const scaleX = baseWidth > maxPrintWidth ? maxPrintWidth / baseWidth : 1
  const scaleY = baseHeight > maxPrintHeight ? maxPrintHeight / baseHeight : 1
  const scale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down

  const containerWidth = baseWidth
  const containerHeight = baseHeight

  // Calculate connector paths
  const connectorPaths = calculateConnectorPaths(rounds, matchPositions, containerWidth)

  // Calculate actual number of registered competitors (not just slots)
  const numCompetitors = countRegisteredCompetitors(bracket.matches)
  const placementPositions = calculatePlacementPositions(numCompetitors)

  // Apply scaling via CSS transform with wrapper to maintain layout
  const scaleStyle = scale < 1 ? `transform: scale(${scale}); transform-origin: top left;` : ''
  const wrapperStyle = scale < 1 ? `width: ${containerWidth * scale}px; height: ${containerHeight * scale}px; overflow: hidden;` : ''
  const wrapperClass = teamBracket ? 'bracket-skeleton-wrapper bracket-skeleton-team' : 'bracket-skeleton-wrapper'
  let bracketHTML = `<div class="${wrapperClass}" style="${wrapperStyle}">`
  bracketHTML += `<div class="bracket-skeleton-container" style="width: ${containerWidth}px; height: ${containerHeight}px; ${scaleStyle}">`

  // Add SVG for connector lines
  bracketHTML += `<svg class="bracket-connectors-skeleton" viewBox="0 0 ${containerWidth} ${containerHeight}" preserveAspectRatio="none">`
  connectorPaths.forEach(path => {
    bracketHTML += `<path d="${path.d}" class="connector-line-skeleton" />`
  })
  bracketHTML += '</svg>'

  // Generate HTML for each round with positioned matches
  rounds.forEach((round, roundIndex) => {
    const isFirstRound = roundIndex === 0
    const roundLeft = 40 + roundIndex * 310 // 250px round width + 60px gap

    bracketHTML += `<div class="bracket-round-skeleton round-${round.number}" style="left: ${roundLeft}px;">`

    if (isFirstRound) {
      // First round: show pairs of competitor entry boxes with competitor full name and club (fixed two-line layout)
      round.matches.forEach((match, matchIndex) => {
        const position = matchPositions[match.id]
        if (!position) return

        bracketHTML += `<div class="match-pair-skeleton" style="position: absolute; top: ${position.top}px; left: 0; width: 250px;" data-match-id="${match.id}">`

        // Competitor 1 box (team bracket: main = club, secondary = competitor names)
        const competitor1Name = getCompetitorName(match.competitorReg)
        const competitor1Club = getCompetitorClub(match.competitorReg)
        bracketHTML += '<div class="competitor-entry-box">'
        bracketHTML += '<div class="competitor-name-section">'
        if (teamBracket) {
          bracketHTML += `<span class="competitor-name-line-skeleton">${escapeHtml(competitor1Club || '—')}</span>`
          if (competitor1Name) bracketHTML += `<span class="competitor-club-skeleton competitor-names-skeleton">${escapeHtml(competitor1Name)}</span>`
        } else {
          bracketHTML += `<span class="competitor-name-line-skeleton">${escapeHtml(competitor1Name || '—')}</span>`
          bracketHTML += `<span class="competitor-club-skeleton">${escapeHtml(competitor1Club || '')}</span>`
        }
        bracketHTML += '</div>'
        bracketHTML += '</div>'

        // Competitor 2 box (stacked below competitor 1)
        const competitor2Name = getCompetitorName(match.competitorTwoReg)
        const competitor2Club = getCompetitorClub(match.competitorTwoReg)
        bracketHTML += '<div class="competitor-entry-box">'
        bracketHTML += '<div class="competitor-name-section">'
        if (teamBracket) {
          bracketHTML += `<span class="competitor-name-line-skeleton">${escapeHtml(competitor2Club || '—')}</span>`
          if (competitor2Name) bracketHTML += `<span class="competitor-club-skeleton competitor-names-skeleton">${escapeHtml(competitor2Name)}</span>`
        } else {
          bracketHTML += `<span class="competitor-name-line-skeleton">${escapeHtml(competitor2Name || '—')}</span>`
          bracketHTML += `<span class="competitor-club-skeleton">${escapeHtml(competitor2Club || '')}</span>`
        }
        bracketHTML += '</div>'
        bracketHTML += '</div>'

        bracketHTML += '</div>'
      })
    } else {
      // Subsequent rounds: show pairs of competitor entry boxes (same as first round)
      round.matches.forEach((match, matchIndex) => {
        const position = matchPositions[match.id]
        if (!position) return

        bracketHTML += `<div class="match-pair-skeleton" style="position: absolute; top: ${position.top}px; left: 0; width: 250px;" data-match-id="${match.id}">`

        // Competitor 1 box
        bracketHTML += '<div class="competitor-entry-box">'
        bracketHTML += '<div class="competitor-name-section"></div>'
        bracketHTML += '<div class="competitor-seed-section"></div>'
        bracketHTML += '</div>'

        // Competitor 2 box (stacked below competitor 1)
        bracketHTML += '<div class="competitor-entry-box">'
        bracketHTML += '<div class="competitor-name-section"></div>'
        bracketHTML += '<div class="competitor-seed-section"></div>'
        bracketHTML += '</div>'

        bracketHTML += '</div>'
      })
    }

    bracketHTML += '</div>'
  })

  // Add winner field after final match
  const finalRound = rounds[rounds.length - 1]
  if (finalRound && finalRound.matches.length > 0) {
    const finalMatch = finalRound.matches[0]
    const finalPosition = matchPositions[finalMatch.id]
    if (finalPosition) {
      // Calculate winner card position; center on connector line (match center Y)
      const winnerLeft = 40 + (rounds.length - 1) * 310 + 250 + 20 // After final round + gap
      const winnerBoxWidth = 180
      const winnerLabelHeight = 20
      const winnerGap = 8
      const winnerEntryHeight = teamBracket ? 100 : 50
      const entryBoxCenterOffset = winnerLabelHeight + winnerGap + winnerEntryHeight / 2
      const connectorLineY = finalPosition.top + finalPosition.height / 2
      const winnerTop = connectorLineY - entryBoxCenterOffset

      const winnerEntryClass = teamBracket ? 'winner-entry-box winner-entry-box-team' : 'winner-entry-box'
      bracketHTML += `<div class="winner-box-skeleton" style="position: absolute; top: ${winnerTop}px; left: ${winnerLeft}px; width: ${winnerBoxWidth}px;">`
      bracketHTML += '<div class="winner-label-skeleton">Pobednik:</div>'
      bracketHTML += `<div class="${winnerEntryClass}">`
      bracketHTML += `<div class="competitor-name-section winner-name-section" style="flex: 1; padding: 8px; min-height: ${teamBracket ? 90 : 34}px;"></div>`
      bracketHTML += '</div>'
      bracketHTML += '</div>'
    }
  }

  bracketHTML += '</div></div>' // Close container and wrapper

  // Add placement section - always show with labels based on number of competitors
  let placementHTML = '<div class="placement-section-skeleton">'
  placementHTML += '<h4 class="placement-title-skeleton">Plasman:</h4>'
  placementHTML += '<div class="placement-fields-skeleton">'

  // Show placement fields based on placementPositions (same logic as BracketDisplay)
  // Labels are above fields to keep all positions in one row
  if (placementPositions >= 1) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">1. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement && placement.first ? escapeHtml(placement.first) : ''}</div>`
    placementHTML += '</div>'
  }

  if (placementPositions >= 2) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">2. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement && placement.second ? escapeHtml(placement.second) : ''}</div>`
    placementHTML += '</div>'
  }

  if (placementPositions >= 3) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">3. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement && placement.third ? escapeHtml(placement.third) : ''}</div>`
    placementHTML += '</div>'
  }

  if (placementPositions >= 4) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">3. mesto #2:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement && placement.third2 ? escapeHtml(placement.third2) : ''}</div>`
    placementHTML += '</div>'
  }

  placementHTML += '</div>'
  placementHTML += '</div>'

  // Add officials section
  const officialsHTML = `
    <div class="signature-section-skeleton">
      <div class="signature-item-skeleton signature-left-skeleton">
        <span class="signature-label-skeleton">${getRecorderLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
      <div class="signature-item-skeleton signature-center-skeleton">
        <span class="signature-label-skeleton">${getVerifiedByLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
      <div class="signature-item-skeleton signature-right-skeleton">
        <span class="signature-label-skeleton">${getChiefJudgeLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
    </div>
  `

  return bracketHTML + placementHTML + officialsHTML
}

function calculateRounds (matches) {
  if (!matches || matches.length === 0) return []

  const roundsMap = {}

  matches.forEach(match => {
    if (!roundsMap[match.round]) {
      roundsMap[match.round] = {
        number: match.round,
        matches: []
      }
    }
    roundsMap[match.round].matches.push(match)
  })

  // Sort matches within each round by matchNumber
  Object.keys(roundsMap).forEach(roundNum => {
    roundsMap[roundNum].matches.sort((a, b) => {
      return (a.matchNumber || 0) - (b.matchNumber || 0)
    })
  })

  // Convert to array and sort by round number
  return Object.values(roundsMap).sort((a, b) => a.number - b.number)
}

function calculateMatchPositions (rounds, teamBracket = false) {
  const positions = {}
  // Team brackets: taller boxes so multiple team member names fit
  const competitorBoxHeight = teamBracket ? 100 : 50
  const competitorGap = teamBracket ? 4 : 2
  const matchHeight = competitorBoxHeight + competitorGap + competitorBoxHeight
  const matchGap = 24

  if (rounds.length === 0) return positions

  // All rounds use the same height (two competitor boxes)
  // First round: position matches vertically
  const firstRound = rounds[0]
  let currentTop = 0
  firstRound.matches.forEach(match => {
    positions[match.id] = {
      top: currentTop,
      height: matchHeight,
      round: match.round
    }
    currentTop += matchHeight + matchGap
  })

  // Subsequent rounds: position based on source matches
  for (let roundIndex = 1; roundIndex < rounds.length; roundIndex++) {
    const currentRound = rounds[roundIndex]
    const previousRound = rounds[roundIndex - 1]

    currentRound.matches.forEach(match => {
      // Find source matches that feed into this match
      const sourceMatches = previousRound.matches.filter(m => m.nextMatchId === match.id)

      if (sourceMatches.length > 0) {
        // Calculate center position based on source matches
        const sourcePositions = sourceMatches.map(m => positions[m.id]).filter(Boolean)
        if (sourcePositions.length > 0) {
          const minTop = Math.min(...sourcePositions.map(p => p.top))
          const maxBottom = Math.max(...sourcePositions.map(p => p.top + p.height))
          const centerY = (minTop + maxBottom) / 2
          positions[match.id] = {
            top: centerY - matchHeight / 2,
            height: matchHeight,
            round: match.round
          }
        } else {
          // Fallback: position sequentially
          positions[match.id] = {
            top: currentTop,
            height: matchHeight,
            round: match.round
          }
          currentTop += matchHeight + matchGap
        }
      } else {
        // No source matches, position sequentially
        positions[match.id] = {
          top: currentTop,
          height: matchHeight,
          round: match.round
        }
        currentTop += matchHeight + matchGap
      }
    })
  }

  return positions
}

function calculateConnectorPaths (rounds, matchPositions, containerWidth) {
  const paths = []
  const roundWidth = 250
  const roundGap = 60
  const roundLeft = 40

  for (let roundIndex = 0; roundIndex < rounds.length - 1; roundIndex++) {
    const currentRound = rounds[roundIndex]
    const nextRound = rounds[roundIndex + 1]
    const targetToSources = {}

    currentRound.matches.forEach(sourceMatch => {
      if (sourceMatch.isBye && !sourceMatch.competitorReg && !sourceMatch.competitorTwoReg) return
      if (!sourceMatch.nextMatchId) return
      if (!targetToSources[sourceMatch.nextMatchId]) targetToSources[sourceMatch.nextMatchId] = []
      targetToSources[sourceMatch.nextMatchId].push(sourceMatch)
    })

    Object.keys(targetToSources).forEach(targetMatchId => {
      const sourceMatches = targetToSources[targetMatchId]
      const targetMatch = nextRound.matches.find(m => String(m.id) === String(targetMatchId))
      if (!targetMatch || sourceMatches.length === 0) return

      const targetPos = matchPositions[targetMatch.id]
      if (!targetPos) return

      const sourcePositions = sourceMatches
        .map(m => matchPositions[m.id])
        .filter(Boolean)

      if (sourcePositions.length === 0) return

      // Calculate connector path
      const sourceX = roundLeft + roundIndex * (roundWidth + roundGap) + roundWidth
      const targetX = roundLeft + (roundIndex + 1) * (roundWidth + roundGap)

      // Center Y of each match (so connectors align for any box height)
      const getMatchCenterY = (pos) => pos.top + pos.height / 2 // Center between the two competitor boxes

      if (sourcePositions.length === 1) {
        // Single source: simple L-shaped path
        const sourceY = getMatchCenterY(sourcePositions[0])
        const targetY = getMatchCenterY(targetPos)
        const midX = (sourceX + targetX) / 2
        paths.push({
          d: `M ${sourceX} ${sourceY} L ${midX} ${sourceY} L ${midX} ${targetY} L ${targetX} ${targetY}`
        })
      } else {
        // Multiple sources: T-shaped path
        sourcePositions.sort((a, b) => a.top - b.top)
        const minY = getMatchCenterY(sourcePositions[0])
        const maxY = getMatchCenterY(sourcePositions[sourcePositions.length - 1])
        const verticalMidY = (minY + maxY) / 2
        const targetY = getMatchCenterY(targetPos)
        const verticalX = sourceX + (targetX - sourceX) * 0.5

        // Horizontal lines from sources
        sourcePositions.forEach(pos => {
          const y = getMatchCenterY(pos)
          paths.push({
            d: `M ${sourceX} ${y} L ${verticalX} ${y}`
          })
        })

        // Vertical line connecting sources
        paths.push({
          d: `M ${verticalX} ${minY} L ${verticalX} ${maxY}`
        })

        // Horizontal line to target
        paths.push({
          d: `M ${verticalX} ${verticalMidY} L ${targetX} ${verticalMidY}`
        })

        // Vertical line to target
        paths.push({
          d: `M ${targetX} ${verticalMidY} L ${targetX} ${targetY}`
        })
      }
    })
  }

  // Add connector from final match to winner box (center Y of match)
  const finalRound = rounds[rounds.length - 1]
  if (finalRound && finalRound.matches.length > 0) {
    const finalMatch = finalRound.matches[0]
    const finalPos = matchPositions[finalMatch.id]
    if (finalPos) {
      const finalX = roundLeft + (rounds.length - 1) * (roundWidth + roundGap) + roundWidth
      const finalY = finalPos.top + finalPos.height / 2
      const winnerCardLeft = 40 + (rounds.length - 1) * 310 + 250 + 20
      paths.push({
        d: `M ${finalX} ${finalY} L ${winnerCardLeft} ${finalY}`
      })
    }
  }

  return paths
}

function getRecorderLabel (tournament) {
  // Use i18n if available, otherwise default
  return 'Zapisničar'
}

function getVerifiedByLabel (tournament) {
  return 'Kontrolisao'
}

function getChiefJudgeLabel (tournament) {
  return 'Glavni sudija'
}

function countRegisteredCompetitors (matches) {
  if (!matches || matches.length === 0) return 0

  const competitorIds = new Set()

  // Count unique competitors from first round matches
  matches.forEach(match => {
    if (match.round === 1) {
      if (match.competitorRegId && !competitorIds.has(match.competitorRegId)) {
        competitorIds.add(match.competitorRegId)
      }
      if (match.competitorTwoRegId && !competitorIds.has(match.competitorTwoRegId)) {
        competitorIds.add(match.competitorTwoRegId)
      }
    }
  })

  return competitorIds.size
}

function calculatePlacementPositions (numCompetitors) {
  if (numCompetitors === 0) return 0
  if (numCompetitors === 1) return 1
  if (numCompetitors === 2) return 2
  if (numCompetitors === 3) return 3
  return 4 // 4 or more competitors
}

function escapeHtml (text) {
  if (!text) return ''
  // Simple HTML escaping without DOM manipulation
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Builds the page header used on every printed page (bracket, scoring sheet).
 * Renders the tournament name above the category/discipline title, plus the
 * optional tatami badge. Styles are inline so the markup also works when it is
 * injected into a cloned print document that has no stylesheet of its own.
 */
export function generatePrintPageHeaderHTML ({ tournamentName, title, subtitle, tatami } = {}) {
  const name = escapeHtml(tournamentName)
  const heading = escapeHtml(title)
  const sub = escapeHtml(subtitle)
  const tatamiText = tatami != null && tatami !== '' ? escapeHtml(String(tatami)) : ''

  if (!name && !heading && !sub && !tatamiText) return ''

  // Font sizes/weights mirror .competitor-list-* in common/print.js so the
  // competitor table page and the bracket pages share one header look.
  const nameHTML = name
    ? `<div style="margin:0 0 4px 0;font-size:20px;font-weight:700;color:#222;">${name}</div>`
    : ''
  const headingHTML = heading
    ? `<h2 style="margin:0 0 8px 0;font-size:18px;font-weight:bold;color:#333;">${heading}</h2>`
    : ''
  const subtitleHTML = sub
    ? `<div style="margin:0;font-size:14px;color:#555;">${sub}</div>`
    : ''
  const tatamiHTML = tatamiText
    ? `<div style="flex-shrink:0;font-size:16px;font-weight:500;color:#333;">Tatami: ${tatamiText}</div>`
    : ''

  return `
    <div style="margin:0 0 30px 0;padding:0 10mm 15px 10mm;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;box-sizing:border-box;position:relative;border-bottom:2px solid #333;">
      <div style="flex:1;min-width:0;">${nameHTML}${headingHTML}${subtitleHTML}</div>
      ${tatamiHTML}
    </div>
  `
}

/**
 * Builds the "Discipline | Category" heading used on print pages. Gender is a
 * separate line (see buildBracketGenderText) so every printed page of a bracket
 * carries the same header as the competitor table page.
 */
export function buildBracketPrintTitle (bracket, categoryName, disciplineName) {
  return [disciplineName, categoryName].filter(Boolean).join(' | ')
}

/**
 * Gender line shown under the print heading; empty for mixed brackets.
 */
export function buildBracketGenderText (bracket) {
  if (!bracket || !bracket.gender || bracket.gender === 'mixed') return ''
  return bracket.gender === 'male' ? 'Muški' : 'Ženski'
}

/**
 * Generates HTML content for bracket skeleton, placement, and officials
 * Returns just the content HTML (not a full document) to be used with openPrint
 */
export function generateBracketPrintHTML (bracket, placementData, tournament, title, categoryName, disciplineName, tatami) {
  // Get placement labels from bracket if placementData is just IDs
  const placement = {}
  if (placementData) {
    if (typeof placementData === 'object' && !Array.isArray(placementData)) {
      // If placementData contains labels (strings), use them directly
      if (placementData.first && typeof placementData.first === 'string') {
        placement.first = placementData.first
      }
      if (placementData.second && typeof placementData.second === 'string') {
        placement.second = placementData.second
      }
      if (placementData.third && typeof placementData.third === 'string') {
        placement.third = placementData.third
      }
      if (placementData.third2 && typeof placementData.third2 === 'string') {
        placement.third2 = placementData.third2
      }
    }
  }

  const bracketHTML = generateBracketSkeletonHTML(bracket, placement, tournament)

  // Build header: tournament name + category/discipline title + optional Tatami badge.
  const headerHTML = generatePrintPageHeaderHTML({
    tournamentName: tournament?.name,
    title: title || buildBracketPrintTitle(bracket, categoryName, disciplineName),
    subtitle: buildBracketGenderText(bracket),
    tatami
  })

  // Return HTML content with inline styles (openPrint will wrap it in a document)
  return `
    <style>
      .bracket-header-skeleton {
        margin-bottom: 30px;
        padding: 0 10mm 15px 10mm;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        box-sizing: border-box;
        position: relative;
      }

      .bracket-header-skeleton::after {
        content: "";
        position: absolute;
        left: 10mm;
        right: 10mm;
        bottom: 0;
        border-bottom: 2px solid #333;
      }

      .bracket-header-skeleton__left {
        flex: 1;
        min-width: 0;
      }

      .tatami-skeleton {
        flex-shrink: 0;
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }

      .bracket-title-skeleton {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #555;
      }

      .bracket-skeleton-wrapper {
        position: relative;
        margin: 20px 0;
      }

      .bracket-skeleton-container {
        position: relative;
        overflow: visible;
      }

      .bracket-connectors-skeleton {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1;
      }

      .connector-line-skeleton {
        fill: none;
        stroke: #333;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .bracket-round-skeleton {
        position: absolute;
        top: 0;
        z-index: 2;
      }

      .match-pair-skeleton {
        position: absolute;
        display: flex;
        flex-direction: column;
      }

      .competitor-entry-box {
        width: 100%;
        min-height: 50px;
        max-height: 50px;
        height: 50px;
        border: 1px solid #333;
        margin-bottom: 2px;
        display: flex;
        position: relative;
        background: white;
        box-sizing: border-box;
        overflow: hidden;
      }

      .competitor-name-section {
        flex: 1;
        padding: 6px 8px;
        min-height: 34px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        justify-content: center;
        box-sizing: border-box;
        overflow: hidden;
      }

      .competitor-name-line-skeleton {
        display: block;
        font-size: 18px;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .competitor-club-skeleton {
        display: block;
        font-size: 14px;
        color: #555;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .competitor-names-skeleton {
        font-size: 9px;
        color: #666;
        line-height: 1.3;
        white-space: normal;
      }

      /* Team brackets: larger competitor boxes so all team member names fit */
      .bracket-skeleton-wrapper.bracket-skeleton-team .competitor-entry-box {
        min-height: 100px;
        max-height: 100px;
        height: 100px;
        margin-bottom: 4px;
      }

      .bracket-skeleton-wrapper.bracket-skeleton-team .competitor-name-section {
        min-height: 92px;
        padding: 8px 10px;
        font-size: 11px;
        line-height: 1.6;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      .bracket-skeleton-wrapper.bracket-skeleton-team .winner-entry-box-team {
        min-height: 100px;
      }

      .bracket-skeleton-wrapper.bracket-skeleton-team .winner-name-section {
        min-height: 90px;
        font-size: 11px;
        line-height: 1.6;
      }

      .winner-box-skeleton {
        position: absolute;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .winner-label-skeleton {
        font-size: 12px;
        font-weight: bold;
        color: #333;
        margin-bottom: 4px;
      }

      .winner-entry-box {
        width: 100%;
        min-height: 50px;
        border: 1px solid #333;
        display: flex;
        background: white;
      }

      .placement-section-skeleton {
        margin-top: 40px;
        padding: 20px 40px;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
      }

      .placement-title-skeleton {
        margin: 0 0 20px 0;
        font-size: 16px;
        font-weight: bold;
        color: #333;
      }

      .placement-fields-skeleton {
        display: flex;
        gap: 12px;
        flex-wrap: nowrap;
        align-items: flex-start;
        max-width: 100%;
      }

      .placement-field-skeleton {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        text-align: center;
      }

      .placement-label-skeleton {
        font-size: 14px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 4px;
        text-align: center;
      }

      .placement-box-skeleton {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        min-height: 40px;
        padding: 4px 8px;
        border: 1px solid #333;
        background: white;
        box-sizing: border-box;
        text-align: center;
        white-space: pre-line;
      }

      .signature-section-skeleton {
        margin-top: 40px;
        padding: 20px 40px;
        width: 100%;
        display: flex;
        justify-content: space-between;
        gap: 20px;
      }

      .signature-item-skeleton {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }

      .signature-left-skeleton {
        align-items: flex-start;
      }

      .signature-right-skeleton {
        align-items: flex-end;
      }

      .signature-center-skeleton {
        align-items: center;
      }

      .signature-label-skeleton {
        font-size: 12px;
        color: #333;
        font-weight: 500;
      }

      .signature-line-skeleton {
        width: 100%;
        height: 1px;
        border-top: 1px solid #333;
        margin-top: 40px;
      }

      @media print {
        .bracket-skeleton-container {
          page-break-inside: avoid;
        }

        .placement-section-skeleton {
          page-break-inside: avoid;
        }

        .signature-section-skeleton {
          page-break-inside: avoid;
        }

        /* Increase font size for competitor names in first round */
        .round-1 .competitor-name-section {
          font-size: 18px;
        }
      }
    </style>
    ${headerHTML}
    ${bracketHTML}
  `
}

/**
 * Generates HTML content for Bodovanje (scoring) print: header, scoring table, placement, and officials.
 * Used when draw model is 'scoring'. Competitor table is appended by the caller (same as Kostur).
 */
export function generateScoringPrintHTML (bracket, placementData, tournament, categoryName, disciplineName, tatami, competitorNames, scoreRows) {
  const placement = {}
  if (placementData && typeof placementData === 'object' && !Array.isArray(placementData)) {
    if (placementData.first && typeof placementData.first === 'string') placement.first = placementData.first
    if (placementData.second && typeof placementData.second === 'string') placement.second = placementData.second
    if (placementData.third && typeof placementData.third === 'string') placement.third = placementData.third
    if (placementData.third2 && typeof placementData.third2 === 'string') placement.third2 = placementData.third2
  }

  const headerHTML = generatePrintPageHeaderHTML({
    tournamentName: tournament?.name,
    title: buildBracketPrintTitle(bracket, categoryName, disciplineName),
    subtitle: buildBracketGenderText(bracket),
    tatami
  })

  const placementPositions = !competitorNames || competitorNames.length === 0
    ? 0
    : Math.min(4, Math.max(1, competitorNames.length))

  let tableHTML = '<div class="bodovanje-print-section"><table class="bodovanje-table-print">'
  tableHTML += '<thead><tr>'
  tableHTML += '<th class="bodovanje-col-rb">Rb.</th>'
  tableHTML += '<th class="bodovanje-col-name">Ime i prezime</th>'
  tableHTML += '<th class="bodovanje-col-disciplina"></th>'
  tableHTML += '<th class="bodovanje-col-score">S1</th><th class="bodovanje-col-score">S2</th><th class="bodovanje-col-score">S3</th>'
  tableHTML += '<th class="bodovanje-col-score">S4</th><th class="bodovanje-col-score">S5</th><th class="bodovanje-col-score">S6</th>'
  tableHTML += '<th class="bodovanje-col-zbir">Zbir</th><th class="bodovanje-col-plasman">Plasman</th>'
  tableHTML += '</tr></thead><tbody>'
  // scoreRows (optional, index-aligned with competitorNames) carries the scores already entered on
  // screen: { values: [s1..s6], total, rank }. Without it the sheet prints blank, as before, which
  // is still what you want for a bracket the judges have not scored yet.
  ;(competitorNames || []).forEach((name, index) => {
    const row = (scoreRows && scoreRows[index]) || {}
    const values = Array.isArray(row.values) ? row.values : []
    tableHTML += '<tr>'
    tableHTML += `<td class="bodovanje-col-rb">${index + 1}</td>`
    tableHTML += `<td class="bodovanje-col-name">${escapeHtml(name)}</td>`
    tableHTML += '<td class="bodovanje-col-disciplina"></td>'
    for (let i = 0; i < 6; i++) {
      const value = values[i]
      tableHTML += `<td class="bodovanje-col-score">${value === null || value === undefined || value === '' ? '' : escapeHtml(String(value))}</td>`
    }
    const total = row.total === null || row.total === undefined ? '' : String(row.total)
    const rank = row.rank === null || row.rank === undefined ? '' : String(row.rank)
    tableHTML += `<td class="bodovanje-col-zbir">${escapeHtml(total)}</td><td class="bodovanje-col-plasman">${escapeHtml(rank)}</td>`
    tableHTML += '</tr>'
  })
  tableHTML += '</tbody></table></div>'

  let placementHTML = '<div class="placement-section-skeleton">'
  placementHTML += '<h4 class="placement-title-skeleton">Plasman:</h4>'
  placementHTML += '<div class="placement-fields-skeleton">'
  if (placementPositions >= 1) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">1. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement.first ? escapeHtml(placement.first) : ''}</div>`
    placementHTML += '</div>'
  }
  if (placementPositions >= 2) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">2. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement.second ? escapeHtml(placement.second) : ''}</div>`
    placementHTML += '</div>'
  }
  if (placementPositions >= 3) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">3. mesto:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement.third ? escapeHtml(placement.third) : ''}</div>`
    placementHTML += '</div>'
  }
  if (placementPositions >= 4) {
    placementHTML += '<div class="placement-field-skeleton">'
    placementHTML += '<div class="placement-label-skeleton">3. mesto #2:</div>'
    placementHTML += `<div class="placement-box-skeleton">${placement.third2 ? escapeHtml(placement.third2) : ''}</div>`
    placementHTML += '</div>'
  }
  placementHTML += '</div></div>'

  const officialsHTML = `
    <div class="signature-section-skeleton">
      <div class="signature-item-skeleton signature-left-skeleton">
        <span class="signature-label-skeleton">${getRecorderLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
      <div class="signature-item-skeleton signature-center-skeleton">
        <span class="signature-label-skeleton">${getVerifiedByLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
      <div class="signature-item-skeleton signature-right-skeleton">
        <span class="signature-label-skeleton">${getChiefJudgeLabel(tournament)}</span>
        <span class="signature-line-skeleton"></span>
      </div>
    </div>
  `

  return `
    <style>
      .bodovanje-print-wrapper {
        padding: 0 15mm;
        box-sizing: border-box;
        max-width: 100%;
      }
      .bodovanje-print-wrapper .bracket-header-skeleton {
        margin-bottom: 30px;
        padding: 0 0 15px 0;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 20px;
        box-sizing: border-box;
        position: relative;
      }
      .bodovanje-print-wrapper .bracket-header-skeleton::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        border-bottom: 2px solid #333;
      }
      .bodovanje-print-wrapper .bracket-header-skeleton__left { flex: 1; min-width: 0; }
      .bodovanje-print-wrapper .tatami-skeleton { flex-shrink: 0; font-size: 16px; font-weight: 500; color: #333; }
      .bodovanje-print-wrapper .bracket-title-skeleton { margin: 0; font-size: 20px; font-weight: 600; color: #555; }
      .bodovanje-print-wrapper .bodovanje-print-section { margin: 20px 0; overflow: hidden; }
      .bodovanje-print-wrapper .bodovanje-table-print {
        width: 100%;
        max-width: 100%;
        border-collapse: collapse;
        font-size: 14px;
        table-layout: fixed;
        box-sizing: border-box;
      }
      .bodovanje-print-wrapper .bodovanje-table-print th,
      .bodovanje-print-wrapper .bodovanje-table-print td {
        border: 1px solid #333;
        padding: 6px 8px;
        text-align: left;
        box-sizing: border-box;
      }
      .bodovanje-print-wrapper .bodovanje-table-print th { background-color: #f5f7fa; font-weight: 600; text-align: center; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-rb { width: 5%; text-align: center; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-name { width: 26%; overflow: hidden; text-overflow: ellipsis; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-disciplina { width: 17%; overflow: hidden; text-overflow: ellipsis; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-score { width: 5%; text-align: center; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-zbir { width: 7%; text-align: center; }
      .bodovanje-print-wrapper .bodovanje-table-print .bodovanje-col-plasman { width: 10%; text-align: center; padding-right: 10px; box-sizing: border-box; }
      .bodovanje-print-wrapper .placement-title-skeleton { margin: 0 0 20px 0; font-size: 16px; font-weight: bold; color: #333; }
      .bodovanje-print-wrapper .placement-fields-skeleton { display: flex; gap: 12px; flex-wrap: nowrap; align-items: flex-start; max-width: 100%; }
      .bodovanje-print-wrapper .placement-field-skeleton { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; gap: 8px; text-align: center; }
      .bodovanje-print-wrapper .placement-label-skeleton { font-size: 14px; color: #606266; font-weight: 500; margin-bottom: 4px; text-align: center; }
      .bodovanje-print-wrapper .placement-box-skeleton { display: block; width: 100%; max-width: 100%; min-width: 0; min-height: 40px; padding: 4px 8px; border: 1px solid #333; background: white; box-sizing: border-box; text-align: center; white-space: pre-line; }
      .bodovanje-print-wrapper .signature-section-skeleton { margin-top: 40px; padding: 20px 0; width: 100%; display: flex; justify-content: space-between; gap: 20px; }
      .bodovanje-print-wrapper .signature-item-skeleton { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 10px; }
      .bodovanje-print-wrapper .signature-left-skeleton { align-items: flex-start; }
      .bodovanje-print-wrapper .signature-right-skeleton { align-items: flex-end; }
      .bodovanje-print-wrapper .signature-center-skeleton { align-items: center; }
      .bodovanje-print-wrapper .signature-label-skeleton { font-size: 12px; color: #333; font-weight: 500; }
      .bodovanje-print-wrapper .signature-line-skeleton { width: 100%; height: 1px; border-top: 1px solid #333; margin-top: 40px; }
      @media print {
        .bodovanje-print-wrapper .placement-section-skeleton { page-break-inside: avoid; }
        .bodovanje-print-wrapper .signature-section-skeleton { page-break-inside: avoid; }
      }
    </style>
    <div class="bodovanje-print-wrapper">
    ${headerHTML}
    ${tableHTML}
    ${placementHTML}
    ${officialsHTML}
    </div>
  `
}
